import React, { useState } from 'react';
import isEmpty from 'validator/lib/isEmpty';

import { useUpdatePasswordMutation } from '../../../../Auth/api';
import { Button } from '../../../../GlobalComponents/Button';
import { ButtonContainer, Form } from '../../../../GlobalComponents/Form';
import { Input } from '../../../../GlobalComponents/Input';
import { Title } from '../../../../GlobalComponents/Title';
import { ErrorMessage } from '../../../../GlobalComponents/ErrorMessage';
import { SuccessMessage } from '../../../../GlobalComponents/SuccessMessage';
import { useAppSelector } from '../../../../reduxstore/hooks';

const initialValue: ChangePasswordProps = {
	currentPassword: '',
	newPassword: '',
	retypePassword: '',
	errors: null
};

const ChangePassword: React.FC = () => {
	const [changePassword, { isLoading, isError, isSuccess, error }] =
		useUpdatePasswordMutation();
	const [formData, setFormData] = useState<ChangePasswordProps>(initialValue);

	const handleChanges = (event: React.ChangeEvent<HTMLInputElement>) => {
		const target = event.target as HTMLInputElement;
		setFormData({
			...formData,
			[target.name]: target.value
		});
	};

	const userId = useAppSelector(state => state.authReducer.id);

	const validateForm = (): boolean => {
		const errors: IErrors = {};
		let formIsValid = true;

		if (isEmpty(formData.currentPassword)) {
			formIsValid = false;
			errors['currentPassword'] = 'Password cannot be empty';
		} else {
			errors['currentPassword'] = null;
		}

		if (isEmpty(formData.newPassword)) {
			formIsValid = false;
			errors['newPassword'] = 'New Password cannot be empty';
		} else if (formData.newPassword.length < 7) {
			formIsValid = false;
			errors['newPassword'] = `Passwords must be at least 7 characters.`;
		} else if (formData.newPassword === formData.currentPassword) {
			formIsValid = false;
			errors['newPassword'] =
				'New password cannot be the same as current password';
		} else {
			errors['newPassword'] = null;
		}

		if (isEmpty(formData.retypePassword)) {
			formIsValid = false;
			errors['retypePassword'] = 'Password cannot be empty';
		} else if (formData.newPassword !== formData.retypePassword) {
			formIsValid = false;
			errors['retypePassword'] = 'Passwords do not match';
		} else {
			errors['retypePassword'] = null;
		}

		setFormData(prevState => ({ ...prevState, errors: errors }));
		return formIsValid;
	};

	const handleSubmit = (event: any): void => {
		event?.preventDefault();

		try {
			if (validateForm()) {
				changePassword({
					id: userId,
					old_password: formData.currentPassword,
					new_password: formData.newPassword
				})
					.unwrap()
					.then(() => {
						setFormData({ ...formData, errors: null });
					});
			}
		} catch {
			setFormData({
				...formData
			});
			console.error('Error - ChangePassword - submit');
			// what error would be caught here?
		}
	};

	return (
		<>
			<Title>Change Password</Title>
			<Form $noColumn={true} onSubmit={handleSubmit}>
				{isError && <ErrorMessage error={error}></ErrorMessage>}
				{isSuccess && (
					<SuccessMessage message="Password has been successfully changed." />
				)}

				<Input
					name="currentPassword"
					label="Current Password"
					type="password"
					value={formData?.currentPassword}
					onChange={handleChanges}
					$error={
						formData.errors && formData.errors['currentPassword']
					}
				/>
				<Input
					name="newPassword"
					label="New Password"
					type="password"
					value={formData?.newPassword}
					onChange={handleChanges}
					$error={
						formData.errors ? formData.errors['newPassword'] : null
					}
				/>
				<Input
					name="retypePassword"
					label="Re-type New Password"
					type="password"
					value={formData?.retypePassword}
					onChange={handleChanges}
					$error={
						formData.errors
							? formData.errors['retypePassword']
							: null
					}
				/>
				<ButtonContainer>
					<Button
						type="submit"
						$isLoading={isLoading}
						disabled={isLoading}
					>
						CHANGE PASSWORD
					</Button>
				</ButtonContainer>
			</Form>
		</>
	);
};

export default ChangePassword;

interface IErrors {
	[key: string]: string | null;
}

interface ChangePasswordProps {
	currentPassword: string;
	newPassword: string;
	retypePassword: string;
	errors: IErrors | null;
}
